import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import './CancelOrderDialog.scss';

export const SUPPORT_EMAIL = '2kiddo.bg@gmail.com';

interface ICancelOrderDialogProps {
  open: boolean
  orderId: number | null
  /** buyer: optional reason dropdown; admin: required free-text reason. */
  mode: 'buyer' | 'admin'
  isLoading: boolean
  hasError: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}

const BUYER_REASON_KEYS = ['changedMind', 'foundElsewhere', 'wrongOrder', 'other'] as const;

// Shared by the buyer's orders list and the admin orders tab. MUI Dialog
// provides the focus trap, focus return and Escape handling; "Не, запази" is
// the autofocused default so Enter on open never cancels an order.
const CancelOrderDialog = ({
  open,
  orderId,
  mode,
  isLoading,
  hasError,
  onClose,
  onConfirm
}: ICancelOrderDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const textId = useId();
  const [reasonKey, setReasonKey] = useState<string>('');
  const [adminReason, setAdminReason] = useState<string>('');

  useEffect(() => {
    if (open) {
      setReasonKey('');
      setAdminReason('');
    }
  }, [open, orderId]);

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const confirmDisabled = isLoading || (mode === 'admin' && !adminReason.trim());

  const handleConfirm = () => {
    if (confirmDisabled) return;

    onConfirm(mode === 'admin'
      ? adminReason.trim()
      : reasonKey
        ? t(`cancelOrder.reasons.${reasonKey}`)
        : t('cancelOrder.defaultBuyerReason'));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      className="cancel-order-dialog"
      aria-labelledby={titleId}
      aria-describedby={textId}
    >
      <div className="cancel-order-dialog-content">
        <h2 id={titleId} className="cancel-order-dialog-title">
          {t('cancelOrder.title', { id: orderId })}
        </h2>
        <p id={textId} className="cancel-order-dialog-text">
          {mode === 'buyer' ? t('cancelOrder.buyerText') : t('cancelOrder.adminText')}
        </p>

        {mode === 'buyer'
          ? (
            <label className="cancel-order-dialog-field">
              <span>{t('cancelOrder.reasonOptional')}</span>
              <select
                value={reasonKey}
                disabled={isLoading}
                onChange={(e) => {
                  setReasonKey(e.target.value);
                }}
                data-testid="cancel-reason-select"
              >
                <option value="">{t('cancelOrder.noReason')}</option>
                {BUYER_REASON_KEYS.map((key) => (
                  <option key={key} value={key}>{t(`cancelOrder.reasons.${key}`)}</option>
                ))}
              </select>
            </label>
            )
          : (
            <label className="cancel-order-dialog-field">
              <span>{t('cancelOrder.reasonRequired')}</span>
              <textarea
                rows={3}
                value={adminReason}
                disabled={isLoading}
                onChange={(e) => {
                  setAdminReason(e.target.value);
                }}
                data-testid="admin-cancel-reason"
              />
            </label>
            )}

        {hasError && (
          <div className="cancel-order-dialog-error" role="alert">
            <span>{t('cancelOrder.refused')}</span>
            {mode === 'buyer' && (
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            )}
          </div>
        )}

        <div className="cancel-order-dialog-actions">
          <button
            type="button"
            className="cancel-order-btn cancel-order-btn-secondary"
            onClick={handleClose}
            disabled={isLoading}
            autoFocus
          >
            {t('cancelOrder.keep')}
          </button>
          <button
            type="button"
            className="cancel-order-btn cancel-order-btn-danger"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            aria-busy={isLoading}
          >
            {isLoading ? t('cancelOrder.cancelling') : t('cancelOrder.confirm')}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CancelOrderDialog;
